import { Helmet } from "react-helmet";
import { connect, useDispatch, useSelector } from "react-redux";
import { State } from "redux/store";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import {
  getFilteredNavigationCluster,
  getFilteredNavigationClusterTags,
  getFilteredSimilarityClusters,
  getIsCardslistLoading,
  getIsExplorationPerformanceEnabled,
  getIsImageUploading,
  getIsUserConfigLoading,
  getNavigationClusterLoader,
  getSimilarityVertexCluster,
  getTotalPagesForCluster,
} from "redux/SingleDataset/selectors";
import {
  getImageData,
  getImageLabels,
  getShowLearnMoreModal,
} from "redux/Modals/selectors";
import {
  fetchClusterPageIssueTypes,
  fetchClusterPagePagination,
  fetchContextualDatasetTags,
  fetchDataPagePagination,
  fetchDataset,
  fetchDatasetContextualLabels,
  fetchDatasetIssues,
  fetchDatasetLabels,
  fetchDatasetTags,
  fetchFilterConfig,
  fetchForAsyncDataPage,
  fetchMetadataSources,
  fetchNavigationCluster,
  fetchSimilarityData,
  fetchSimilarityThresholdsInfo,
  fetchTreeFolder,
  fetchUserTags,
  resetStore,
  setCardslistLoading,
  setDatasetLabels,
  setNavigationCluster,
  setNavigationContext,
  setScrollApplied,
  setSimilarityData,
  setSimilarityVertex,
  setTreeFolder,
} from "redux/SingleDataset/actions";
import { fetchImageInfo, fetchImageUniquenessData } from "redux/Modals/actions";
import {
  CLUSTER_DETAILS_PAGE,
  DATA_PAGE,
  IMAGE_DETAILS_PAGE,
} from "helpers/constants/pages";
import {
  calculateTreeFolder,
  convertURItoNumber,
} from "helpers/utility/utilities";
import {
  ENTITY_TYPE_PARAMETER,
  FLOW_PARAMETER,
  FOLDER_PARAMETER,
  LABEL_CLASS_PARAMETER,
  OBJECT_ID_QUERY_PARAMETER,
  PAGE_PARAMETER,
  REVIEW_PARAMETER,
  SEED_PARAMETER,
  VERTEX_ID,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";

import ContainerHeader from "views/components/ContainerHeader";
import HealthStatus from "views/components/HealthStatus";
import StatsFooter from "./StatsFooter";
import ImageView from "./ImageView";
import CardsView from "./CardsView";

import styles from "./style.module.scss";
import { EntityType, isValidEntityType } from "types";
import { LearnMoreModal } from "views/modals/LearnMoreModal";
import useQueryParam from "hooks/useQueryParam";
import { DataPageRightPanelProvider } from "../../providers/DataPageRightPanelProvider";
import AddMediaCompletedModal from "../../components/AddMediaCompletedModal";

const DataPage = ({
  clusters,
  isCardListLoading,
  navigationCluster,
  navigationClusterTags,
  navigationClusterLoader,
  imageData,
  labels,
  isImageUploading,
  showLearnMoreModal,
}: DataPageProps) => {
  const [reviewCounters, setReviewCounters] =
    useState<React.ReactElement | null>(null);
  const queryParamsFilters = useAllQueryParamsFilters();
  const [commonDataFetched, setCommonDataFetched] = useState(false);
  const [isDataSourcesLoading, setIsDataSourcesLoading] = useState(true);
  const { datasetId, clusterId, imageId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const selectedLabelID = useQueryParam(OBJECT_ID_QUERY_PARAMETER);
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeValue: EntityType = isValidEntityType(entityParam)
    ? entityParam
    : "IMAGES";

  const isDataPage = !!datasetId && !imageId && !clusterId;
  const isClusterPage = !!datasetId && !!clusterId && !imageId;
  const isImagePage = !!imageId;

  const dynamicPage = isImagePage
    ? IMAGE_DETAILS_PAGE
    : isClusterPage
      ? CLUSTER_DETAILS_PAGE
      : DATA_PAGE;

  const navigate = useNavigate();
  const singleDatasetState = useSelector((state: State) => state.singleDataset);
  const isUserConfigLoading = useSelector(getIsUserConfigLoading);
  const isExplorationPerformanceEnabled = useSelector(
    getIsExplorationPerformanceEnabled,
  );

  const createParams = (searchParams: URLSearchParams, filterConfig: any) => {
    const params: { [key: string]: any } = {
      [FOLDER_PARAMETER]: null,
      [PAGE_PARAMETER]: null,
    };

    searchParams.forEach((value: string, key: string) => {
      switch (key) {
        case FOLDER_PARAMETER:
          params[FOLDER_PARAMETER] = decodeURIComponent(value);
          break;
        case PAGE_PARAMETER:
          if (convertURItoNumber(value)) {
            params[PAGE_PARAMETER] = convertURItoNumber(value);
          }
          break;
        default:
          break;
      }
    });

    return params;
  };
  const createStateFromParams = (params: any, state: any) => {
    Object.keys(params).forEach((key: string) => {
      switch (key) {
        case FOLDER_PARAMETER:
          //Ugly Hack:
          //refer to calculateTreeFolder in helpers/utility/utilities.ts
          if (params[key]) {
            const path =
              params[key].charAt(0) === "/" //if the first char is a slash remove it
                ? params[key].slice(1)
                : params[key];

            const pathValues = path.split("/");
            const pathHasChildren = pathValues.length > 1;
            if (pathHasChildren) {
              const treeFolder = calculateTreeFolder(path, state.treeFolder);
              dispatch(setTreeFolder(treeFolder));
            }
          }
          break;
        case PAGE_PARAMETER:
          // Navigation logic moved to separate effect to avoid circular dependency
          break;
      }
    });
  };

  const fetchCommonData = async () => {
    try {
      dispatch(setCardslistLoading(true));
      dispatch(setDatasetLabels([]));
      await Promise.all([
        // items in this list are blocking the page loading so the list should be the bare minimum that is required
        //  to render the page and not nothing else (additional settings can be loaded in the background)
        dispatch(fetchSimilarityThresholdsInfo(datasetId!) as any),
        dispatch(fetchFilterConfig(datasetId) as any),
      ]);
      setCommonDataFetched(true);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchForDataPage = (datasetID: string) => {
    dispatch(setCardslistLoading(true));
    dispatch(setSimilarityData(null));
    dispatch(fetchDataset(datasetId) as any);
    dispatch(
      fetchDatasetContextualLabels(datasetId, queryParamsFilters) as any,
    );
    dispatch(fetchDataPagePagination(datasetID, queryParamsFilters) as any);
    dispatch(fetchSimilarityData(datasetId, queryParamsFilters) as any);
    dispatch(fetchDatasetIssues(datasetId, queryParamsFilters) as any);
  };
  const fetchForClusterPage = (datasetID: string, clusterID: string) => {
    dispatch(fetchDataset(datasetId, clusterId) as any);
    dispatch(
      fetchDatasetContextualLabels(
        datasetId,
        queryParamsFilters,
        clusterId,
      ) as any,
    );
    dispatch(fetchUserTags(datasetId, queryParamsFilters, clusterId) as any);
    dispatch(
      fetchClusterPageIssueTypes(
        datasetId,
        clusterId,
        queryParamsFilters,
      ) as any,
    );
    dispatch(
      fetchClusterPagePagination(
        datasetID,
        clusterID,
        queryParamsFilters,
      ) as any,
    );
    dispatch(
      fetchNavigationCluster(datasetID, clusterID, queryParamsFilters) as any,
    );
    if (isExplorationPerformanceEnabled) {
      fetchRightPanelData();
    }
  };
  const fetchForImagePage = (imageID: string) => {
    dispatch(fetchDataset(datasetId) as any);
    dispatch(fetchImageInfo(datasetId!, imageID, selectedLabelID) as any);
    dispatch(fetchImageUniquenessData(datasetId!, imageID) as any);
  };

  const fetchRightPanelData = async () => {
    if (!isExplorationPerformanceEnabled) {
      return;
    }
    await dispatch(fetchMetadataSources(datasetId!, entityTypeValue) as any);
    setIsDataSourcesLoading(false);
  };

  useEffect(() => {
    fetchCommonData();
    //eslint-disable-next-line
  }, []);

  // URL initialization (runs once to set page=1 if missing)
  useEffect(() => {
    if (!searchParams.has(PAGE_PARAMETER) && !isImagePage) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(PAGE_PARAMETER, "1");

      let path = `${location.pathname}?${newSearchParams.toString()}`;
      navigate(path, { replace: true });
    }
    //eslint-disable-next-line
  }, [location.pathname, isImagePage, navigate]);

  // Main data fetching (runs when page parameter exists)
  useEffect(() => {
    if (commonDataFetched) {
      if (!isExplorationPerformanceEnabled) {
        dispatch(fetchDatasetTags(datasetId, entityTypeValue) as any);
        dispatch(fetchDatasetLabels(datasetId, entityTypeValue) as any);
        dispatch(fetchMetadataSources(datasetId!, entityTypeValue) as any);
      }
      dispatch(fetchTreeFolder(datasetId) as any);
    }
  }, [
    commonDataFetched,
    datasetId,
    entityTypeValue,
    dispatch,
    isExplorationPerformanceEnabled,
  ]);

  const searchParamsForLoadingData = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete(SEED_PARAMETER);
    params.delete(FLOW_PARAMETER);
    return params.toString();
  }, [searchParams]);

  useEffect(() => {
    const run = async () => {
      if (
        commonDataFetched &&
        (searchParams.has(PAGE_PARAMETER) || isImagePage)
      ) {
        const params = createParams(
          searchParams,
          singleDatasetState.filterConfig,
        );
        createStateFromParams(params, singleDatasetState);

        if (isImagePage) {
          fetchForImagePage(imageId);
          dispatch(setNavigationContext(IMAGE_DETAILS_PAGE) as any);
          if (clusterId) {
            dispatch(
              fetchNavigationCluster(
                datasetId!,
                clusterId,
                queryParamsFilters,
              ) as any,
            );
          }
        } else if (params[PAGE_PARAMETER] || params[VERTEX_ID]) {
          if (isClusterPage) {
            fetchForClusterPage(datasetId, clusterId);
            dispatch(setNavigationContext(CLUSTER_DETAILS_PAGE) as any);
          } else if (isDataPage) {
            // In review mode with a selected label, ReviewList owns navigationCluster
            const isReviewLabelView =
              searchParams.has(REVIEW_PARAMETER) &&
              searchParams.has(LABEL_CLASS_PARAMETER);
            if (!isReviewLabelView) {
              dispatch(setNavigationCluster(null));
            }
            if (!isExplorationPerformanceEnabled) {
              fetchForDataPage(datasetId);
            } else {
              await dispatch(
                fetchForAsyncDataPage(datasetId, queryParamsFilters) as any,
              );
            }

            dispatch(setNavigationContext(DATA_PAGE) as any);
          }
        }
        dispatch(
          fetchContextualDatasetTags(datasetId, queryParamsFilters) as any,
        );
      }
    };

    run();
    // eslint-disable-next-line
  }, [
    commonDataFetched,
    datasetId,
    clusterId,
    imageId,
    searchParamsForLoadingData,
  ]);

  useEffect(() => {
    const handlePopState = () => {
      //applies scroll on back navigation from browser
      dispatch(setScrollApplied(false));
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      dispatch(resetStore());
    };
  }, [dispatch]);

  const currentVertexCluster = useSelector(getSimilarityVertexCluster);
  useEffect(() => {
    const hasVertexParams =
      queryParamsFilters[VERTEX_ID] || queryParamsFilters[VERTEX_TYPE];

    if (!hasVertexParams && currentVertexCluster) {
      // Clear vertex state when vertex parameters are not present in URL but state still exists
      // This handles browser navigation where URL changes but Redux state doesn't
      dispatch(setSimilarityVertex(null));
    }
  }, [queryParamsFilters, currentVertexCluster, dispatch]);

  return (
    <DataPageRightPanelProvider>
      <Helmet>
        <title>Dataset Exploration</title>
      </Helmet>
      <div className={styles.headerContainer}>
        <ContainerHeader
          dynamicPage={dynamicPage}
          isCardListLoading={isCardListLoading}
          navigationClusterLoader={navigationClusterLoader}
        />
        <HealthStatus />
      </div>

      {showLearnMoreModal && <LearnMoreModal />}
      {dynamicPage === IMAGE_DETAILS_PAGE ? (
        <ImageView labels={labels} imageData={imageData} />
      ) : (
        <>
          <CardsView
            loaders={{
              sources: isDataSourcesLoading,
            }}
            clusters={clusters}
            dynamicPage={dynamicPage}
            navigationCluster={navigationCluster}
            navigationClusterTags={navigationClusterTags}
            isCardListLoading={isCardListLoading}
            navigationClusterLoader={navigationClusterLoader}
            metadataSummaryEnabled
            isImageUploading={isImageUploading}
            setReviewCounters={setReviewCounters}
            reviewCounters={reviewCounters}
          />
          {!isUserConfigLoading && (
            <StatsFooter reviewCounters={reviewCounters} />
          )}
        </>
      )}
      <AddMediaCompletedModal />
    </DataPageRightPanelProvider>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    isCardListLoading: getIsCardslistLoading(state),
    clusters: getFilteredSimilarityClusters(state),
    navigationCluster: getFilteredNavigationCluster(state),
    navigationClusterTags: getFilteredNavigationClusterTags(state),
    navigationClusterLoader:
      getNavigationClusterLoader(state) || !getTotalPagesForCluster(state),
    imageData: getImageData(state),
    labels: getImageLabels(state),
    isImageUploading: getIsImageUploading(state),
    showLearnMoreModal: getShowLearnMoreModal(state),
  };
};

export default connect(mapStatesToProps)(DataPage);

interface DataPageProps {
  labels: any;
  clusters: any;
  imageData: any;
  navigationCluster: any;
  navigationClusterTags: any;
  isImageUploading: boolean;
  isCardListLoading: boolean;
  navigationClusterLoader: boolean;
  showLearnMoreModal: boolean;
}
